import { api } from '../api/client';
import type { MultiSelectOption } from '../components/MultiSelect';

/**
 * A season the admin can attach a reusable (season-division) membership to. It is
 * a superset of `MultiSelectOption` (keeps `_id`/`name` so existing MultiSelect
 * callers still work) and additionally carries the season's term so a caller can
 * drive the DivisionSchemeField live preview from a selected season's dates.
 */
export interface SeasonOption extends MultiSelectOption {
  _id: number;
  name: string;
  termStart: string | null;
  termEnd: string | null;
}

/**
 * Build the flat list of seasons an admin can attach a reusable (season-division)
 * membership to. Seasons belong to courses, so we fan out over the course list
 * and each course's seasons. Labelled with the course name + term range so a
 * season is identifiable in the picker, and each option carries the raw term
 * (`termStart`/`termEnd`) so the membership editor can preview split dates
 * against a chosen season. Best-effort: a failing course is skipped rather than
 * aborting the whole list.
 */
export async function loadSeasonOptions(): Promise<SeasonOption[]> {
  const coursesRes = await api.courses.list();
  const courses = (coursesRes.data as Array<{ _id: number; name: string }>) ?? [];

  const perCourse = await Promise.all(
    courses.map(async (course) => {
      try {
        const { data } = await api.courses.seasons.list(course._id);
        return (data.seasons ?? []).map((s) => {
          const term = [s.termStart, s.termEnd].filter(Boolean).join(' – ');
          return {
            _id: s.id,
            name: term ? `${course.name} · ${term}` : course.name,
            termStart: s.termStart,
            termEnd: s.termEnd,
          } as SeasonOption;
        });
      } catch {
        return [] as SeasonOption[];
      }
    }),
  );

  return perCourse.flat();
}
